import isArray from "lodash/isArray";
import isNil from "lodash/isNil";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AgButtonDownloadTable from "../../components/agGrid/AgButtonDownloadTable";

import TitledHeaderWithSearch from "../../components/layout/TitledHeaderWithSearch";
import { useBorrowerSubmissionsHook } from "../../hooks/useBorrowerSubmissionsHook";
import { usePipelineHook } from "../../hooks/usePipelineHook";
import { cachedDataItemSet } from "../../redux/actions/cachedDataActions";
import { loanPipelineSet } from "../../redux/actions/loanPipelineActions";
import size from "lodash/size";
import Box from "@mui/material/Box";
import { grey } from "@mui/material/colors";
import Flx from "../../components/layout/Flx";
import Screen from "../../components/layout/Screen";
import ToggleTabSwitcher from "../../components/navigation/ToggleTabSwitcher";
import BorrowerApplicationLinksButton from "../../components/ui/buttons/BorrowerApplicationLinksButton";
import CreditAuthsTable from "./CreditAuthsTable";

const CreditAuthSubmissionsScreen = () => {
  const ref = useRef();
  const [quickFilter, setQuickFilter] = useState(null);
  const [tab, setTab] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Use ref to track fetching state to prevent closure issues
  const isFetchingRef = useRef(false);
  const isPipelineFetchingRef = useRef(false);

  const creditAuths = useSelector((state) => state?.cachedData?.creditAuths);
  const loanPipeline = useSelector((state) => state?.loanPipeline);

  const { getCreditAuthorizations } = useBorrowerSubmissionsHook();
  const { getUserPipeline } = usePipelineHook();
  const dispatch = useDispatch();

  // Memoize filtered data to prevent unnecessary recalculations
  const filteredUnassigned = useMemo(() => {
    if (!isArray(creditAuths)) return [];

    return creditAuths.filter(
      (app) => isNil(app?.salesperson) || isNil(app?.loanNumber)
    );
  }, [creditAuths]);

  const filteredAssigned = useMemo(() => {
    if (!isArray(creditAuths)) return [];

    return creditAuths.filter(
      (app) => !isNil(app?.salesperson) && !isNil(app?.loanNumber)
    );
  }, [creditAuths]);

  const tabList = useMemo(() => {
    return [
      `Requires Assignment (${size(filteredUnassigned)})`,
      `Assigned Applications (${size(filteredAssigned)})`,
      `All Credit Authorizations (${size(creditAuths)})`,
    ];
  }, [filteredAssigned, filteredUnassigned, creditAuths]);

  const filteredRowData = useMemo(() => {
    if (!isArray(creditAuths)) return [];

    switch (tab) {
      case 0:
        return filteredUnassigned;

      case 1:
        return filteredAssigned;

      // case "All Credit Authorizations":
      default:
        return creditAuths;
    }
  }, [creditAuths, tab, filteredUnassigned, filteredAssigned]);

  // Fetch pipeline data once if not available
  useEffect(() => {
    console.log("UF=PIPELINE");
    if (isArray(loanPipeline) || isPipelineFetchingRef.current === true) {
      return;
    }
    if (!isArray(loanPipeline) && !isPipelineFetchingRef.current) {
      isPipelineFetchingRef.current = true;

      getUserPipeline({
        onSuccessFn: (pipeline) => {
          dispatch(loanPipelineSet(pipeline));
          isPipelineFetchingRef.current = false;
        },
        onFailFn: () => {
          console.error("Failed to get pipeline");
          // Set to empty array to prevent infinite attempts
          dispatch(loanPipelineSet([]));
          isPipelineFetchingRef.current = false;
        },
      });
    }
  }, [loanPipeline]); // Only depend on loanPipeline

  // Fetch credit authorizations when creditAuths is null
  useEffect(() => {
    // Check if creditAuths is null and we're not already fetching
    if (isNil(creditAuths) && !isFetchingRef.current) {
      isFetchingRef.current = true;
      setIsLoading(true);

      getCreditAuthorizations({
        onSuccessFn: (data) => {
          dispatch(cachedDataItemSet("creditAuths", data));
          isFetchingRef.current = false;
          setIsLoading(false);
        },
        onFailFn: (err) => {
          console.error("GetCreditAuthorizations error:", err);
          // Set to empty array to prevent infinite loop
          dispatch(cachedDataItemSet("creditAuths", []));
          isFetchingRef.current = false;
          setIsLoading(false);
        },
      });
    }
  }, [creditAuths]); // Only depend on creditAuths

  return (
    <Screen>
      <TitledHeaderWithSearch
        title={"Credit Authorization Submissions"}
        quickFilter={quickFilter}
        setQuickFilter={setQuickFilter}
        endContent={
          <Flx g={1}>
            <BorrowerApplicationLinksButton>
              Send Credit Auth
            </BorrowerApplicationLinksButton>
            <AgButtonDownloadTable
              ref={ref}
              downloadName="Credit Authorizations"
            />
          </Flx>
        }
      />

      <Box
        sx={{
          background: grey[100],
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <ToggleTabSwitcher
          sx={{ ml: 1 }}
          value={tab}
          useIndex
          variant={"underline"}
          onChange={setTab}
          tabs={tabList}
        />
      </Box>

      <CreditAuthsTable
        rowData={filteredRowData}
        quickFilter={quickFilter}
        isLoading={isLoading}
      />
    </Screen>
  );
};

export default CreditAuthSubmissionsScreen;
